import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import styled, { withTheme, css } from 'styled-components';

import SearchInputLabelledMulti from '@ui/inputs/SearchInputLabelledMulti.tsx';

import { getHeaderPadding } from '../../../../utils/theming.ts';
import AddWaypointButton from '../DirectionsControls/AddWaypointButton.tsx';
import LogoAndCloseButton from '../DirectionsControls/LogoAndCloseButton.tsx';
import { InputsFooter, directionsHeaderSpacing } from '../DirectionsResultControls/styles.ts';
import {
  handleAddStop,
  handleDragEnd,
  handleDragOver,
  handleDragStart,
  handleRemoveClick,
  handleMoveClick,
  handleClearButtonClick,
} from '../helpers/handleInputChangeEvents.ts';
import WheelchairAccessibilityToggle from '../helpers/WheelchairAccessibilityToggle.tsx';
import useMapSearchInputsToState from '../hooks/useMapSearchInputsToState.ts';
import useOnUpdateSearchInputs from '../hooks/useOnUpdateSearchInputs.ts';

const WidgetWrapper = styled.div`
  width: 100%;
  padding: ${({ $isMobile, $hasLogoUrl, theme }) => getHeaderPadding($isMobile, $hasLogoUrl, theme)};
  ${({ $fullRoutePreviewEnabled }) => $fullRoutePreviewEnabled && directionsHeaderSpacing};
`;

const InputAndAddWrapper = styled.div`
  ${({ $isMobile }) => {
    return $isMobile
      ? css`
          width: 100%;
        `
      : null;
  }}
`;

const DirectionsSearchControlsViewMulti = ({
  currentInputIndex,
  onInputSelect,
  searchInputs,
  logoUrl,
  clientName,
  theme,
  onInputClearButtonClick,
  onSingleInputValueChange,
  onBackButtonClicked,
  handleKeyPress,
  handleSearchInputsChange,
  isMobile,
  fullRoutePreviewEnabled = false,
  T,
  widgetState,
  onSwitchRouteTypeButtonClicked,
  routeAccessibiltyChoice,
}) => {
  const [internalSearchInputs, setInternalSearchInputs] = useState(searchInputs);
  const [draggingSearchInputs, setDraggingSearchInputs] = useState(searchInputs);
  const [isDragging, setIsDragging] = useState(false);

  // Register sensors explicitly so the keyboard sensor uses dnd-kit's sortable
  // coordinate getter. Without it, arrow keys nudge the dragged item by a fixed
  // 25px instead of hopping to the next sortable item, so a single ArrowDown may
  // not cross into the neighbour's droppable region (height-dependent flake) and
  // keyboard reordering effectively breaks for accessibility users.
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Incoming app state
  useMapSearchInputsToState(currentInputIndex, searchInputs, setInternalSearchInputs, setDraggingSearchInputs);

  // Outgoing update to app state
  useOnUpdateSearchInputs(searchInputs, internalSearchInputs, handleSearchInputsChange);

  return (
    <WidgetWrapper
      data-testid={'DirectionsSearchControlsView'}
      $hasLogoUrl={typeof logoUrl !== 'undefined' && !isMobile}
      $isMobile={isMobile}
      $fullRoutePreviewEnabled={fullRoutePreviewEnabled === true}
    >
      <LogoAndCloseButton
        logoUrl={logoUrl}
        theme={theme}
        isMobile={isMobile}
        clientName={clientName}
        onClose={onBackButtonClicked}
        T={T}
      />
      <InputAndAddWrapper $isMobile={isMobile}>
        <DndContext
          sensors={sensors}
          modifiers={[restrictToVerticalAxis]}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart(internalSearchInputs, setDraggingSearchInputs, setIsDragging)}
          onDragOver={handleDragOver(internalSearchInputs, setDraggingSearchInputs)}
          onDragEnd={handleDragEnd(setInternalSearchInputs, setIsDragging)}
          onDragCancel={() => setIsDragging(false)}
        >
          <SortableContext items={internalSearchInputs} strategy={verticalListSortingStrategy}>
            {internalSearchInputs.map((input, index) => {
              let placeholder = `${T('getDirectionsFromTo:Add stop')}:`;
              if (index === 0) {
                placeholder = `${T('getDirectionsFromTo:From')}:`;
              }
              if (index === internalSearchInputs.length - 1) {
                placeholder = `${T('getDirectionsFromTo:To')}:`;
              }
              const first = isDragging ? input.id === draggingSearchInputs[0].id : index === 0;
              const last = isDragging
                ? draggingSearchInputs[internalSearchInputs.length - 1].id === input.id
                : index === internalSearchInputs.length - 1;
              return (
                <SearchInputLabelledMulti
                  data-testid={'SearchInputLabelledMulti'}
                  index={index}
                  stopNumber={isDragging ? draggingSearchInputs.findIndex(item => item.id === input.id) : index}
                  searchInputs={searchInputs}
                  id={input.id} // Both this prop and 'key' prop seem to need to have the same value
                  key={input.id || index}
                  autoFocus={currentInputIndex === index}
                  value={input.term ? input.term : ''}
                  placeholder={placeholder}
                  onInputChange={onSingleInputValueChange}
                  onInputSelect={onInputSelect}
                  onClick={() => onInputSelect(index)}
                  onInputClearButtonClick={handleClearButtonClick(
                    internalSearchInputs,
                    setInternalSearchInputs,
                    onInputClearButtonClick,
                    index,
                  )}
                  clickableItemOptions={!(internalSearchInputs?.length < 3)}
                  handleRemoveClick={handleRemoveClick(
                    internalSearchInputs,
                    setInternalSearchInputs,
                    index,
                    widgetState,
                  )}
                  handleMoveClick={handleMoveClick(internalSearchInputs, setInternalSearchInputs, index, widgetState)}
                  handleKeyPress={handleKeyPress}
                  draggable={true}
                  first={first}
                  last={last}
                  T={T}
                  isMobile={isMobile}
                />
              );
            })}
          </SortableContext>
        </DndContext>

        <InputsFooter>
          <AddWaypointButton
            disabled={true}
            text={T('getDirectionsFromTo:Fill Empty Destination')}
            T={T}
            theme={theme}
            handleAddStop={handleAddStop(internalSearchInputs, setInternalSearchInputs, onInputSelect)}
          />
          <WheelchairAccessibilityToggle
            routeAccessibiltyChoice={routeAccessibiltyChoice}
            onSwitchRouteTypeButtonClicked={onSwitchRouteTypeButtonClicked}
            T={T}
            theme={theme}
          />
        </InputsFooter>
      </InputAndAddWrapper>
    </WidgetWrapper>
  );
};

DirectionsSearchControlsViewMulti.propTypes = {
  onInputSelect: PropTypes.func.isRequired,
  searchInputs: PropTypes.arrayOf(PropTypes.object).isRequired,
  logoUrl: PropTypes.string,
  logoLinkUrl: PropTypes.string,
  clientName: PropTypes.string,
  theme: PropTypes.object,
  onInputClearButtonClick: PropTypes.func.isRequired,
  onSingleInputValueChange: PropTypes.func.isRequired,
  onBackButtonClicked: PropTypes.func.isRequired,
  handleKeyPress: PropTypes.func.isRequired,
  handleSearchInputsChange: PropTypes.func.isRequired,
  isMobile: PropTypes.bool,
  fullRoutePreviewEnabled: PropTypes.bool,
  T: PropTypes.func.isRequired,
};

export default withTheme(DirectionsSearchControlsViewMulti);
